import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useChannelAccounts } from "@/components/AccountTag";
import { TemplateForm } from "@/components/TemplateForm";
import { ChatTeardropText, Trash, Plus, ArrowClockwise } from "@phosphor-icons/react";

const STATUS = { APPROVED: ["Aprobada", "bg-emerald-500/15 text-emerald-700"], PENDING: ["En revisión", "bg-amber-500/15 text-amber-700"],
  REJECTED: ["Rechazada", "bg-red-500/15 text-red-700"], PAUSED: ["Pausada", "bg-zinc-500/15 text-zinc-700"], DISABLED: ["Desactivada", "bg-zinc-500/15 text-zinc-700"] };
const imgSrc = (u) => (u && u.startsWith("/api/") ? `${API_BASE.replace(/\/api$/, "")}${u}` : u);

const TemplateRow = ({ t, onDelete }) => {
  const [label, cls] = STATUS[t.status] || [t.status, "bg-zinc-500/15"];
  return (
    <div className="border border-border rounded-lg p-3 flex gap-3" data-testid={`template-row-${t.name}`}>
      {t.header_format === "IMAGE" && t.header_image_url && <img src={imgSrc(t.header_image_url)} alt="" className="w-16 h-16 rounded object-cover shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold">{t.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${cls}`} data-testid={`template-status-${t.name}`}>{label}</span>
          <Badge variant="outline" className="text-[10px]">{t.category === "UTILITY" ? "Utilidad" : "Marketing"} · {t.language}</Badge>
        </div>
        {t.header_text && <div className="text-xs font-semibold mt-1">{t.header_text}</div>}
        <div className="text-xs text-muted-foreground whitespace-pre-wrap mt-1 line-clamp-3">{t.body}</div>
        {t.footer && <div className="text-[10px] text-muted-foreground mt-1">{t.footer}</div>}
        {t.status === "REJECTED" && t.rejected_reason && t.rejected_reason !== "NONE" && (
          <div className="text-[11px] text-red-600 mt-1" data-testid={`template-rejected-${t.name}`}>Motivo de Meta: {t.rejected_reason}</div>
        )}
      </div>
      <Button size="icon" variant="ghost" onClick={() => onDelete(t)} data-testid={`template-delete-${t.name}`} title="Borrar en Meta">
        <Trash size={15} className="text-red-600" />
      </Button>
    </div>
  );
};

export function TemplateManager() {
  const accounts = useChannelAccounts().filter((a) => a.channel === "whatsapp" && a.active !== false);
  const [open, setOpen] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const defaultAcc = (accounts.find((a) => a.is_default) || accounts[0])?.id || "";
  useEffect(() => { if (open && !accountId && defaultAcc) setAccountId(defaultAcc); }, [open, defaultAcc, accountId]);
  const params = accountId ? { channel_account_id: accountId } : {};
  const load = () => {
    setList(null); setError(null);
    api.get("/wa-templates", { params }).then(({ data }) => setList(data.templates))
      .catch((e) => { setList([]); setError(e?.response?.data?.detail || "No se pudieron leer las plantillas de Meta"); });
  };
  useEffect(() => { if (open && (accountId || !accounts.length)) load(); }, [open, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (t) => {
    if (!window.confirm(`¿Borrar la plantilla «${t.name}» en Meta? Las campañas que la usen dejarán de poder enviarse.`)) return;
    try { await api.delete(`/wa-templates/${t.name}`, { params }); toast.success("Plantilla borrada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Meta no permitió borrarla"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreating(false); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-1.5" data-testid="templates-open"><ChatTeardropText size={16} /> Plantillas</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle>Plantillas de WhatsApp</DialogTitle>
          <DialogDescription>Crea plantillas aquí y Meta las revisa (de minutos a 24 h). Solo las aprobadas se pueden usar en campañas.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          {accounts.length > 1 && (
            <Select value={accountId} onValueChange={(v) => { setAccountId(v); setCreating(false); }}>
              <SelectTrigger className="w-64" data-testid="templates-account"><SelectValue placeholder="Número" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}{a.is_default ? " (principal)" : ""}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={load} className="gap-1" data-testid="templates-refresh"><ArrowClockwise size={14} /> Actualizar estado</Button>
          {!creating && <Button size="sm" onClick={() => setCreating(true)} className="gap-1 ml-auto" data-testid="templates-new"><Plus size={14} /> Nueva plantilla</Button>}
        </div>
        {creating && <TemplateForm accountId={accountId} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
        {error && <div className="text-xs text-red-600" data-testid="templates-error">{error}</div>}
        {!list ? <div className="text-sm text-muted-foreground py-4">Cargando…</div> : (
          <div className="space-y-2" data-testid="templates-list">
            {list.length === 0 && !error && <div className="text-sm text-muted-foreground py-4">No hay plantillas en esta cuenta.</div>}
            {list.map((t) => <TemplateRow key={`${t.name}-${t.language}`} t={t} onDelete={remove} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TemplateManager;
